import React, { useState, useEffect } from 'react';
import {
  useEncomendas,
  useCriarEncomenda,
  useAtualizarEncomenda,
  useRemoverEncomenda,
  useAlterarStatusEncomenda,
  useEncomenda,
} from '../../hooks/useEncomendas';
import { useClientes } from '../../hooks/useClientes';
import { useAuth } from '../../contexts/AuthContext';
import { usePageTitle } from '../../hooks/usePageTitle';
import { useProdutos } from '../../hooks/useProdutos';
import { Encomenda, StatusEncomenda, CriarEncomendaRequest } from '../../types';
import ConfirmModal from '../../components/Common/ConfirmModal';

const Encomendas: React.FC = () => {
  usePageTitle('Encomendas');
  const { user } = useAuth();
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [dataFiltro, setDataFiltro] = useState<string>('');
  const [modalAberto, setModalAberto] = useState(false);
  const [encomendaId, setEncomendaId] = useState<number | null>(null);
  const [confirmarRemocao, setConfirmarRemocao] = useState<{
    isOpen: boolean;
    encomendaId: number | null;
    encomendaNome: string;
  }>({ isOpen: false, encomendaId: null, encomendaNome: '' });

  const [confirmarStatus, setConfirmarStatus] = useState<{
    isOpen: boolean;
    encomendaId: number | null;
    novoStatus: StatusEncomenda | null;
    statusAtual: StatusEncomenda | null;
  }>({ isOpen: false, encomendaId: null, novoStatus: null, statusAtual: null });

  const [formData, setFormData] = useState<any>({
    clienteId: 0,
    estabelecimentoId: user?.estabelecimentoId || 1,
    dataHoraEntrega: '',
    retirada: false,
    enderecoEntrega: '',
    observacoes: '',
    desconto: '',
    valorEntrada: '',
    entradaPaga: false,
    itens: [],
  });

  useEffect(() => {
    if (user?.estabelecimentoId) {
      setFormData((prev: any) => ({
        ...prev,
        estabelecimentoId: user.estabelecimentoId!,
      }));
    }
  }, [user]);

  const { data: encomendaData, isLoading } = useEncomendas({
    pagina: paginaAtual,
    tamanhoPagina: 10,
    dataFiltro: dataFiltro || undefined,
  });

  const { data: encomendaDetalhes } = useEncomenda(encomendaId || 0);
  const { data: clientes } = useClientes();
  const { data: produtos } = useProdutos(true);
  const criarEncomendaMutation = useCriarEncomenda();
  const atualizarEncomendaMutation = useAtualizarEncomenda();
  const removerEncomendaMutation = useRemoverEncomenda();
  const alterarStatusMutation = useAlterarStatusEncomenda();

  const encomendas = encomendaData?.encomendas || [];
  const totalPaginas = encomendaData?.totalPaginas || 1;

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    try {
      return new Intl.DateTimeFormat('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(new Date(dateString));
    } catch {
      return dateString;
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const isDentroDeUmaSemana = (dataHoraEntrega: string) => {
    const dataEntrega = new Date(dataHoraEntrega);
    const hoje = new Date();
    const umaSemana = new Date();
    umaSemana.setDate(hoje.getDate() + 7);
    return dataEntrega >= hoje && dataEntrega <= umaSemana;
  };

  const fecharModal = () => {
    setModalAberto(false);
    setEncomendaId(null);
    setFormData({
      clienteId: 0,
      estabelecimentoId: user?.estabelecimentoId || 1,
      dataHoraEntrega: '',
      retirada: false,
      enderecoEntrega: '',
      observacoes: '',
      desconto: '',
      valorEntrada: '',
      entradaPaga: false,
      itens: [],
    });
  };

  // Preenche o formulário quando os detalhes da encomenda são carregados
  useEffect(() => {
    if (encomendaDetalhes && encomendaId) {
      setFormData({
        clienteId: encomendaDetalhes.clienteId,
        estabelecimentoId: user?.estabelecimentoId || 1,
        dataHoraEntrega: new Date(encomendaDetalhes.dataHoraEntrega).toISOString().slice(0, 16),
        retirada: encomendaDetalhes.retirada,
        enderecoEntrega: encomendaDetalhes.enderecoEntrega || '',
        observacoes: encomendaDetalhes.observacoes || '',
        desconto: String(encomendaDetalhes.desconto || ''),
        valorEntrada: String(encomendaDetalhes.valorEntrada || ''),
        entradaPaga: encomendaDetalhes.entradaPaga || false,
        itens: encomendaDetalhes.itens.map((item: any) => ({
          produtoId: item.produtoId,
          quantidade: item.quantidade,
        })),
      });
    }
  }, [encomendaDetalhes, encomendaId, user]);

  const abrirModal = async (id?: number) => {
    if (id) {
      setEncomendaId(id);
      // Os dados serão carregados pelo useEffect quando encomendaDetalhes mudar
    } else {
      setEncomendaId(null);
      setFormData({
        clienteId: 0,
        estabelecimentoId: user?.estabelecimentoId || 1,
        dataHoraEntrega: '',
        retirada: false,
        enderecoEntrega: '',
        observacoes: '',
        desconto: '',
        valorEntrada: '',
        entradaPaga: false,
        itens: [],
      });
    }
    setModalAberto(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.itens.length === 0 && !encomendaId) {
      alert('Adicione pelo menos um produto Ã  encomenda');
      return;
    }

    try {
      const desconto = parseFloat(String(formData.desconto).replace(',', '.')) || 0;
      const valorEntrada = parseFloat(String(formData.valorEntrada).replace(',', '.')) || 0;

      if (encomendaId) {
        // Para atualização, precisa incluir nomeProduto e precoUnitario
        const itensComDados = formData.itens.map((item: any) => {
          const produto = produtos?.find((p) => p.id === item.produtoId);
          if (!produto) {
            throw new Error(`Produto ${item.produtoId} não encontrado`);
          }
          return {
            produtoId: item.produtoId,
            nomeProduto: produto.nome,
            precoUnitario: produto.precoUnitario,
            quantidade: item.quantidade,
          };
        });

        await atualizarEncomendaMutation.mutateAsync({
          id: encomendaId,
          data: {
            dataHoraEntrega: formData.dataHoraEntrega,
            retirada: formData.retirada,
            enderecoEntrega: formData.enderecoEntrega,
            observacoes: formData.observacoes,
            desconto,
            valorEntrada,
            entradaPaga: formData.entradaPaga,
            itens: itensComDados,
          },
        });
      } else {
        await criarEncomendaMutation.mutateAsync({
          clienteId: formData.clienteId,
          estabelecimentoId: formData.estabelecimentoId,
          dataHoraEntrega: formData.dataHoraEntrega,
          retirada: formData.retirada,
          enderecoEntrega: formData.enderecoEntrega,
          observacoes: formData.observacoes,
          desconto,
          valorEntrada,
          entradaPaga: formData.entradaPaga,
          itens: formData.itens,
        });
      }
      fecharModal();
    } catch (error) {
      console.error('Erro ao salvar encomenda:', error);
    }
  };

  const adicionarProduto = () => {
    const produtoId = parseInt((document.getElementById('produtoSelect') as HTMLSelectElement).value);
    if (produtoId === 0) return;

    const produtoExistente = formData.itens.find((item: any) => item.produtoId === produtoId);
    if (produtoExistente) {
      alert('Produto já adicionado Ã  encomenda');
      return;
    }

    setFormData({
      ...formData,
      itens: [...formData.itens, { produtoId, quantidade: 1 }],
    });
  };

  const removerProduto = (produtoId: number) => {
    setFormData({
      ...formData,
      itens: formData.itens.filter((item: any) => item.produtoId !== produtoId),
    });
  };

  const atualizarQuantidade = (produtoId: number, quantidade: number) => {
    if (quantidade < 1) return;
    setFormData({
      ...formData,
      itens: formData.itens.map((item: any) =>
        item.produtoId === produtoId ? { ...item, quantidade } : item
      ),
    });
  };

  const calcularTotal = () => {
    const subtotal = formData.itens.reduce((total: number, item: any) => {
      const produto = produtos?.find((p) => p.id === item.produtoId);
      return total + (produto?.precoUnitario || 0) * item.quantidade;
    }, 0);
    const descontoNum = parseFloat(String(formData.desconto).replace(',', '.')) || 0;
    return subtotal - descontoNum;
  };

  const confirmarRemover = (id: number, clienteNome: string) => {
    setConfirmarRemocao({ isOpen: true, encomendaId: id, encomendaNome: clienteNome });
  };

  const handleConfirmarRemocao = async () => {
    if (confirmarRemocao.encomendaId) {
      await removerEncomendaMutation.mutateAsync(confirmarRemocao.encomendaId);
      fecharConfirmacaoRemocao();
    }
  };

  const fecharConfirmacaoRemocao = () => {
    setConfirmarRemocao({ isOpen: false, encomendaId: null, encomendaNome: '' });
  };

  const solicitarMudancaStatus = (encomendaId: number, statusAtual: StatusEncomenda, novoStatus: StatusEncomenda) => {
    setConfirmarStatus({
      isOpen: true,
      encomendaId,
      novoStatus,
      statusAtual,
    });
  };

  const handleConfirmarMudancaStatus = async () => {
    if (confirmarStatus.encomendaId && confirmarStatus.novoStatus) {
      await alterarStatusMutation.mutateAsync({
        id: confirmarStatus.encomendaId,
        status: confirmarStatus.novoStatus,
      });
      fecharConfirmacaoStatus();
    }
  };

  const fecharConfirmacaoStatus = () => {
    setConfirmarStatus({ isOpen: false, encomendaId: null, novoStatus: null, statusAtual: null });
  };

  const getStatusLabel = (status: StatusEncomenda): string => {
    const labels: { [key in StatusEncomenda]: string } = {
      [StatusEncomenda.Aguardando]: 'Aguardando',
      [StatusEncomenda.EmProducao]: 'Em Produção',
      [StatusEncomenda.Pronto]: 'Pronto',
      [StatusEncomenda.Entregue]: 'Entregue',
    };
    return labels[status];
  };

  const getStatusBadge = (status: StatusEncomenda) => {
    const classes = {
      [StatusEncomenda.Aguardando]: 'bg-yellow-100 text-yellow-800',
      [StatusEncomenda.EmProducao]: 'bg-blue-100 text-blue-800',
      [StatusEncomenda.Pronto]: 'bg-green-100 text-green-800',
      [StatusEncomenda.Entregue]: 'bg-gray-100 text-gray-800',
    };

    const labels = {
      [StatusEncomenda.Aguardando]: 'Aguardando',
      [StatusEncomenda.EmProducao]: 'Em Produção',
      [StatusEncomenda.Pronto]: 'Pronto',
      [StatusEncomenda.Entregue]: 'Entregue',
    };

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${classes[status]}`}>
        {labels[status]}
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">Carregando encomendas...</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-800">Encomendas</h1>
        <button
          onClick={() => abrirModal()}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
        >
          Nova Encomenda
        </button>
      </div>

      {/* Filtros */}
      <div className="mb-6 flex gap-4 items-center">
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700">Filtrar por data:</label>
          <input
            type="date"
            value={dataFiltro}
            onChange={(e) => {
              setDataFiltro(e.target.value);
              setPaginaAtual(1);
            }}
            className="border border-gray-300 rounded-lg px-3 py-2"
          />
          {dataFiltro && (
            <button
              onClick={() => {
                setDataFiltro('');
                setPaginaAtual(1);
              }}
              className="text-sm text-red-600 hover:text-red-800"
            >
              Limpar filtro
            </button>
          )}
        </div>
      </div>

      {/* Tabela */}
      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Cliente
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Data/Hora Entrega
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Tipo/Endereço
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Produtos
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Valor
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Ações
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {encomendas.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-6 py-4 text-center text-gray-500">
                  Nenhuma encomenda encontrada
                </td>
              </tr>
            ) : (
              encomendas.map((encomenda) => (
                <tr
                  key={encomenda.id}
                  className={
                    isDentroDeUmaSemana(encomenda.dataHoraEntrega)
                      ? 'bg-yellow-50 hover:bg-yellow-100'
                      : 'hover:bg-gray-50'
                  }
                >
                  <td className="px-6 py-4 text-sm text-gray-900">{encomenda.clienteNome}</td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {formatDate(encomenda.dataHoraEntrega)}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {encomenda.retirada ? (
                      <span className="text-blue-600 font-medium">Retirada</span>
                    ) : (
                      <div>
                        <div className="text-green-600 font-medium">Entrega</div>
                        {encomenda.enderecoEntrega && (
                          <div className="text-xs text-gray-500 mt-1">{encomenda.enderecoEntrega}</div>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {encomenda.itens && encomenda.itens.length > 0 ? (
                      <div className="space-y-1">
                        {encomenda.itens.map((item, index) => (
                          <div key={index} className="text-xs">
                            {item.quantidade}x {item.produtoNome}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4">{getStatusBadge(encomenda.status as any)}</td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {formatCurrency(encomenda.valorTotal)}
                  </td>
                  <td className="px-6 py-4 text-sm space-x-2">
                    <select
                      value={encomenda.status}
                      onChange={(e) =>
                        solicitarMudancaStatus(
                          encomenda.id,
                          encomenda.status as any,
                          e.target.value as StatusEncomenda
                        )
                      }
                      className="border border-gray-300 rounded px-2 py-1 text-sm"
                    >
                      {Object.values(StatusEncomenda).map((status) => (
                        <option key={status} value={status}>
                          {getStatusLabel(status)}
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={() => abrirModal(encomenda.id)}
                      className="text-blue-600 hover:text-blue-800"
                      disabled={encomenda.status !== StatusEncomenda.Aguardando}
                      title={
                        encomenda.status !== StatusEncomenda.Aguardando
                          ? 'Apenas encomendas aguardando podem ser editadas'
                          : 'Editar encomenda'
                      }
                    >
                      Editar
                    </button>
                    <button
                      onClick={() => confirmarRemover(encomenda.id, encomenda.clienteNome)}
                      className="text-red-600 hover:text-red-800"
                    >
                      Remover
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Paginação */}
      {totalPaginas > 1 && (
        <div className="mt-6 flex justify-center gap-2">
          <button
            onClick={() => setPaginaAtual((p) => Math.max(1, p - 1))}
            disabled={paginaAtual === 1}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Anterior
          </button>
          <span className="px-4 py-2 text-gray-700">
            Página {paginaAtual} de {totalPaginas}
          </span>
          <button
            onClick={() => setPaginaAtual((p) => Math.min(totalPaginas, p + 1))}
            disabled={paginaAtual === totalPaginas}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Próxima
          </button>
        </div>
      )}

      {/* Modal unificado */}
      {modalAberto && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-4">
              {encomendaId ? 'Editar Encomenda' : 'Nova Encomenda'}
            </h2>
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Cliente *</label>
                <select
                  value={formData.clienteId}
                  onChange={(e) => setFormData({ ...formData, clienteId: parseInt(e.target.value) })}
                  required
                  disabled={!!encomendaId}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                >
                  <option value={0}>Selecione um cliente</option>
                  {clientes?.map((cliente) => (
                    <option key={cliente.id} value={cliente.id}>
                      {cliente.nome}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Data e Hora de Entrega *
                </label>
                <input
                  type="datetime-local"
                  value={formData.dataHoraEntrega}
                  onChange={(e) => setFormData({ ...formData, dataHoraEntrega: e.target.value })}
                  required
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>

              <div className="mb-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.retirada}
                    onChange={(e) => {
                      const retirada = e.target.checked;
                      setFormData({
                        ...formData,
                        retirada,
                        enderecoEntrega: retirada ? '' : formData.enderecoEntrega,
                      });
                    }}
                    className="mr-2"
                  />
                  <span className="text-sm font-medium text-gray-700">Cliente irá retirar</span>
                </label>
              </div>

              {!formData.retirada && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Endereço de Entrega *
                  </label>
                  <input
                    type="text"
                    value={formData.enderecoEntrega}
                    onChange={(e) => setFormData({ ...formData, enderecoEntrega: e.target.value })}
                    required={!formData.retirada}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                </div>
              )}

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Desconto (R$)</label>
                <input
                  type="text"
                  value={formData.desconto}
                  onChange={(e) =>
                    setFormData({ ...formData, desconto: e.target.value.replace(/[^0-9.,]/g, '') })
                  }
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  placeholder="0.00"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Valor de Entrada (R$)</label>
                <input
                  type="text"
                  value={formData.valorEntrada}
                  onChange={(e) =>
                    setFormData({ ...formData, valorEntrada: e.target.value.replace(/[^0-9.,]/g, '') })
                  }
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  placeholder="0.00"
                />
              </div>

              <div className="mb-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.entradaPaga}
                    onChange={(e) => setFormData({ ...formData, entradaPaga: e.target.checked })}
                    className="mr-2"
                  />
                  <span className="text-sm font-medium text-gray-700">Entrada paga</span>
                </label>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Observações</label>
                <textarea
                  value={formData.observacoes}
                  onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  rows={3}
                  placeholder="Observações opcionais"
                />
              </div>

              {/* Produtos */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Produtos *
                </label>

                {formData.itens.length > 0 && (
                    <div className="mb-3 border border-gray-200 rounded-lg overflow-hidden">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">
                              Produto
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">
                              Preço Unit.
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">
                              Quantidade
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">
                              Subtotal
                            </th>
                            <th className="px-4 py-2"></th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {formData.itens.map((item: any) => {
                            const produto = produtos?.find((p) => p.id === item.produtoId);
                            if (!produto) return null;
                            return (
                              <tr key={item.produtoId}>
                                <td className="px-4 py-2 text-sm text-gray-900">{produto.nome}</td>
                                <td className="px-4 py-2 text-sm text-gray-900">
                                  {formatCurrency(produto.precoUnitario)}
                                </td>
                                <td className="px-4 py-2">
                                  <input
                                    type="text"
                                    value={item.quantidade || ''}
                                    onChange={(e) => {
                                      const val = e.target.value.replace(/[^0-9]/g, '');
                                      atualizarQuantidade(item.produtoId, parseInt(val) || 1);
                                    }}
                                    className="w-20 border border-gray-300 rounded px-2 py-1"
                                  />
                                </td>
                                <td className="px-4 py-2 text-sm text-gray-900">
                                  {formatCurrency(produto.precoUnitario * item.quantidade)}
                                </td>
                                <td className="px-4 py-2 text-center">
                                  <button
                                    type="button"
                                    onClick={() => removerProduto(item.produtoId)}
                                    className="text-red-600 hover:text-red-800"
                                  >
                                    ×
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                          <tr className="bg-gray-50 font-semibold">
                            <td colSpan={3} className="px-4 py-2 text-right text-sm text-gray-900">
                              Total:
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-900">
                              {formatCurrency(calcularTotal())}
                            </td>
                            <td></td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  )}

                  <div className="flex gap-2">

                        <select

                          id="produtoSelect"
                      className="flex-1 border border-gray-300 rounded-lg px-3 py-2"
                      defaultValue={0}
                    >
                      <option value={0}>Selecione um produto para adicionar</option>
                      {produtos
                        ?.filter((p) => !formData.itens.some((item: any) => item.produtoId === p.id))
                        .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'))
                        .map((produto) => (
                          <option key={produto.id} value={produto.id}>
                            {produto.nome} - R$ {produto.precoUnitario.toFixed(2)}
                          </option>
                        ))}
                    </select>
                    <button
                      type="button"
                      onClick={adicionarProduto}
                      className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
                    >
                      Adicionar
                    </button>
                  </div>

                  {formData.itens.length === 0 && (
                    <p className="text-sm text-gray-500 mt-2">
                      Adicione pelo menos um produto Ã  encomenda
                    </p>
                  )}
              </div>

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={fecharModal}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {encomendaId ? 'Atualizar' : 'Criar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de confirmação de remoção */}
      <ConfirmModal
        isOpen={confirmarRemocao.isOpen}
        title="Confirmar Remoção"
        message={`Tem certeza que deseja remover a encomenda do cliente ${confirmarRemocao.encomendaNome}?`}
        confirmText="Remover"
        cancelText="Cancelar"
        onConfirm={handleConfirmarRemocao}
        onCancel={fecharConfirmacaoRemocao}
      />

      {/* Modal de confirmação de mudança de status */}
      <ConfirmModal
        isOpen={confirmarStatus.isOpen}
        title="Confirmar Alteração de Status"
        message={`Tem certeza que deseja alterar o status de "${confirmarStatus.statusAtual ? getStatusLabel(confirmarStatus.statusAtual) : ''}" para "${confirmarStatus.novoStatus ? getStatusLabel(confirmarStatus.novoStatus) : ''}"?`}
        confirmText="Alterar"
        cancelText="Cancelar"
        onConfirm={handleConfirmarMudancaStatus}
        onCancel={fecharConfirmacaoStatus}
      />
    </div>
  );
};

export default Encomendas;
